import { useMemo } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { cn } from "@/lib/utils";
import type { WorkOrderReservation } from "@/lib/workOrderReservations";
import { isDateBooked } from "@/lib/workOrderReservations";

interface PropertyAvailabilityCalendarProps {
  reservations: WorkOrderReservation[];
  className?: string;
}

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

export function PropertyAvailabilityCalendar({
  reservations,
  className,
}: PropertyAvailabilityCalendarProps) {
  const months = useMemo(() => {
    const base = startOfMonth(new Date());
    return [0, 1, 2].map((i) => addMonths(base, i));
  }, []);

  const today = useMemo(() => startOfDay(new Date()), []);

  return (
    <div className={cn("space-y-3 max-h-[280px] overflow-y-auto pr-1", className)}>
      <div className="flex flex-wrap gap-2 text-[10px]">
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5">
          Available
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 text-red-700 px-2 py-0.5 line-through">
          Booked
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {months.map((monthStart) => {
          const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
          const gridEnd = endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 });
          const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

          return (
            <div key={format(monthStart, "yyyy-MM")} className="min-w-0">
              <p className="text-xs font-medium text-center mb-1">{format(monthStart, "MMM yyyy")}</p>
              <div className="grid grid-cols-7 gap-px text-[9px] text-center text-muted-foreground mb-0.5">
                {WEEKDAYS.map((d, i) => (
                  <span key={`${format(monthStart, "yyyy-MM")}-wd-${i}`}>{d}</span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-px">
                {days.map((day) => {
                  const inMonth = isSameMonth(day, monthStart);
                  const booked = inMonth && isDateBooked(day, reservations);
                  const past = day < today;
                  const todayRing = isToday(day);

                  return (
                    <div
                      key={day.toISOString()}
                      className={cn(
                        "aspect-square flex items-center justify-center rounded-sm text-[9px]",
                        !inMonth && "invisible",
                        inMonth && past && "opacity-40",
                        inMonth && booked && "bg-red-50 text-red-700 line-through",
                        inMonth && !booked && !past && "bg-background border border-border/60",
                        inMonth && !booked && past && "bg-muted/30 text-muted-foreground",
                        todayRing && inMonth && "ring-1 ring-primary/50"
                      )}
                      title={inMonth ? format(day, "MMM d, yyyy") : undefined}
                    >
                      {inMonth ? format(day, "d") : ""}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
